import CardDropdown from "@/components/atoms/CardDropdown";
import Flex from "@/components/atoms/Flex";
import Modal from "@/components/atoms/Modal";
import { INVALID_FORM_MESSAGE } from "@/constants/validatorMessages";
import { getFileIcon, humanFileSize } from "@/helpers/FileHelpers";

import { isIterableArray } from "@/helpers/FormHelpers";
import { CompanyFileForm } from "@/interfaces/Company";
import { IModalChildrenProps, IModalMethods } from "@/interfaces/Modal";
import { useStoreCompanyFilesMutation } from "@/services/companies/files";

import { faCloudUpload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { Col, Dropdown, Form, Row } from "react-bootstrap";
import Dropzone from "react-dropzone";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { ToastContentProps, toast } from "react-toastify";

const StoreCompanyFileModal: ForwardRefRenderFunction<
  IModalMethods,
  IModalChildrenProps<{ companyId: string }>
> = ({ companyId, onHide, callback }, ref) => {
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  const { control, handleSubmit, reset } = useForm<CompanyFileForm>();
  const [storeCompanyFilesAsync] = useStoreCompanyFilesMutation();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "details",
  });

  const showOrHiddenModal = () => ModalRef.current?.showOrHiddenModal();

  useImperativeHandle(ref, () => ({
    showOrHiddenModal,
  }));

  const onSubmit: SubmitHandler<CompanyFileForm> = (data) => {
    showOrHiddenModal();
    toast.promise(storeCompanyFilesAsync([companyId, data]).unwrap, {
      pending: "Upload en cours...",
      success: {
        render({ data }) {
          reset();
          data && callback?.(data);
          return (
            <p style={{ marginBottom: 0, textAlign: "center" }}>
              L'upload est terminé 🤩
            </p>
          );
        },
      },
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          showOrHiddenModal();
          if (data?.status === 422) {
            return "Les champs que vous avez remplis semblent être incorrects.";
          }

          return "Une erreur est survenue";
        },
      },
    });
  };

  const onError: SubmitErrorHandler<CompanyFileForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <Modal
      ref={ModalRef}
      onHide={onHide}
      title="Upload un fichier"
      onSubmit={handleSubmit(onSubmit, onError)}
      requiredLabel
    >
      <Row className="gx-3">
        <Col lg={12}>
          <Controller
            control={control}
            name="files"
            render={({ field }) => (
              <Dropzone
                onDrop={(acceptedFiles) => {
                  field.onChange(acceptedFiles);
                  acceptedFiles.map(() =>
                    append({
                      label: "",
                    })
                  );
                }}
              >
                {({ getRootProps, getInputProps }) => (
                  <div {...getRootProps({ className: "dropzone-area py-6" })}>
                    <input {...getInputProps()} />
                    <Flex justifyContent="center" alignItems="center">
                      <FontAwesomeIcon icon={faCloudUpload} className="me-2" />
                      <p className="fs-0 mb-0 text-700">
                        Déposez vos images ici
                      </p>
                    </Flex>
                  </div>
                )}
              </Dropzone>
            )}
          />
        </Col>
        <Col lg={12}>
          {isIterableArray(fields) && (
            <div className="border-top mt-3">
              <Controller
                control={control}
                name="files"
                render={({ field: files }) => (
                  <>
                    {files.value?.map((file, index) => (
                      <Controller
                        key={file.name}
                        control={control}
                        name={`details.${index}.label`}
                        render={({ field }) => (
                          <Flex
                            alignItems="center"
                            className="py-3 border-bottom btn-reveal-trigger"
                          >
                            <FontAwesomeIcon
                              icon={getFileIcon(file.type)}
                              fontSize={30}
                            />
                            <Flex
                              justifyContent="between"
                              alignItems="center"
                              className="ms-3 flex-1"
                            >
                              <Form.Group className="me-1 w-100">
                                <Form.Control
                                  size={"sm"}
                                  value={field.value}
                                  onChange={field.onChange}
                                  className="border-0 shadow-none ps-0"
                                  placeholder={file.name}
                                />
                                <Flex
                                  className="position-relative"
                                  alignItems="center"
                                >
                                  <p className="mb-0 fs--1 text-400 line-height-1">
                                    {humanFileSize(file.size)}
                                  </p>
                                </Flex>
                              </Form.Group>
                              <CardDropdown>
                                <div className="py-2">
                                  <Dropdown.Item
                                    className="text-danger"
                                    onClick={() => {
                                      const newFiles = [...files.value];
                                      newFiles.splice(index, 1);
                                      files.onChange(newFiles);
                                      remove(index);
                                    }}
                                  >
                                    Supprimer
                                  </Dropdown.Item>
                                </div>
                              </CardDropdown>
                            </Flex>
                          </Flex>
                        )}
                      />
                    ))}
                  </>
                )}
              />
            </div>
          )}
        </Col>
      </Row>
    </Modal>
  );
};

export default forwardRef(StoreCompanyFileModal);
